import { useEffect, useRef, useState } from "react";
import { API_CHAT_URL, getWsUrl } from "../Api/apiconnector";

export default function CoordinatorChat() {
  const containerRefs = useRef({});
  const [rooms, setRooms] = useState([]);
  const [inputs, setInputs] = useState({});
  const socketsRef = useRef({});
  const readyRef = useRef({});
  const [uploadProgress, setUploadProgress] = useState({});
  const messageEndRefs = useRef({});

  // useEffect(() => {
  //   rooms.forEach(room => {
  //     messageEndRefs.current[room.id]?.scrollIntoView({ behavior: "smooth" });
  //   });
  // }, [rooms]);
  useEffect(() => {
    rooms.forEach(room => {
      const container = containerRefs.current[room.id];
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }, [rooms]);

  // Load rooms with messages from API
  useEffect(() => {
    fetchRooms();
  }, []);

  const openFile = async (fileKey) => {
    const res = await fetch(
      `${API_CHAT_URL}/presigned-url/?key=${encodeURIComponent(fileKey)}`,
      { credentials: "include" }
    );
    const data = await res.json();
    window.open(data.url, "_blank");
  };


  const fetchRooms = () => {
    fetch(`${API_CHAT_URL}/rooms/`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        console.log("Fetched rooms with messages:", data);
        setRooms(data.map(room => ({ 
          ...room, 
          messages: room.messages || [],
          connecting: false,
          loadedViaApi: true
        })));
      })
      .catch(error => {
        console.error("Failed to fetch rooms:", error);
      });
  };

  const connectToRoom = (roomId) => {
    if (socketsRef.current[roomId]) {
      console.log(`Already connected to room ${roomId}`);
      return;
    }

    console.log(`Connecting to room ${roomId}...`);
    
    setRooms(prev => prev.map(room => 
      room.id === roomId ? { ...room, connecting: true } : room
    ));

    const ws = new WebSocket(getWsUrl(roomId));

    readyRef.current[roomId] = false;

    ws.onopen = () => {
      console.log(`✅ WebSocket connected for room ${roomId}`);
      readyRef.current[roomId] = true;
      setRooms(prev => prev.map(room => 
        room.id === roomId ? { ...room, connecting: false, loadedViaApi: false } : room
      ));
    };

    ws.onclose = (event) => {
      console.log(`❌ WebSocket closed for room ${roomId}:`, event.code, event.reason);
      readyRef.current[roomId] = false;
      delete socketsRef.current[roomId];
      delete readyRef.current[roomId];
      setRooms(prev => prev.map(room => 
        room.id === roomId ? { ...room, connecting: false } : room
      ));
    };

    ws.onerror = (error) => {
      console.error(`⚠️ WebSocket error for room ${roomId}:`, error);
      readyRef.current[roomId] = false;
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        console.log(`📨 Message from room ${roomId}:`, msg.type || "chat_message");

        setRooms(prev => prev.map(room => {
          if (room.id !== roomId) return room;

          if (msg.type === "history") {
            console.log(`📜 Loaded ${msg.messages?.length || 0} history messages via WebSocket`);
            return { 
              ...room, 
              messages: msg.messages || [],
              loadedViaApi: false
            };
          } else if (msg.type === "chat_message") {
            // Check for duplicate by message_id if available, else by content and timestamp
            const duplicate = room.messages.find(m => 
              (m.message_id && m.message_id === msg.message_id) ||
              (m.sender === msg.sender && m.message === msg.message && m.timestamp === msg.timestamp)
            );
            if (!duplicate) {
              return {
                ...room,
                messages: [...room.messages, {
                  sender: msg.sender,
                  message: msg.message,
                  file: msg.file,
                  timestamp: msg.timestamp,
                  tagged_username: msg.tagged_username,
                  message_id: msg.message_id
                }]
              };
            }
          }
          return room;
        }));
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };

    socketsRef.current[roomId] = ws;
  };

  const uploadFile = (roomId, file) => {
    if (!file) return;
  
    const ws = socketsRef.current[roomId];
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      alert("Chat not connected");
      return;
    }
  
    const formData = new FormData();
    formData.append("file", file);
    formData.append("room_id", roomId); // 🔥 REQUIRED
  
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_CHAT_URL}/upload/`, true);
    xhr.withCredentials = true;
  
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(prev => ({ ...prev, [roomId]: percent }));
      }
    };
  
    xhr.onload = () => {
      setUploadProgress(prev => ({ ...prev, [roomId]: null }));
  
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
  
        ws.send(JSON.stringify({
          message: "",
          file: data.file_key,
          message_id: data.message_id
        }));
      } else {
        alert("Upload failed");
      }
    };
  
    xhr.onerror = () => {
      setUploadProgress(prev => ({ ...prev, [roomId]: null }));
      alert("Upload error");
    };
  
    xhr.send(formData);
  };


  const sendMessage = (roomId) => {
    const message = inputs[roomId]?.trim();
    if (!message) return;

    const ws = socketsRef.current[roomId];
    if (ws && ws.readyState === WebSocket.OPEN) {
      console.log(`📤 Sending message to room ${roomId}:`, message);
      ws.send(JSON.stringify({ message: message }));
      setInputs(prev => ({ ...prev, [roomId]: "" }));
    } else {
      console.error(`Cannot send message: WebSocket not open for room ${roomId}`);
      alert("Connection not ready. Try again in a moment.");
    }
  };

  const closeRoom = async (roomId) => {
    if (!window.confirm("Are you sure you want to close this chat? This will end the conversation.")) {
      return;
    }

    if (socketsRef.current[roomId]) {
      socketsRef.current[roomId].close(1000, "Chat closed by coordinator");
      delete socketsRef.current[roomId];
      delete readyRef.current[roomId];
    }

    try {
      // const response = await fetch(`${API_CHAT_URL}/rooms/${roomId}/close/`, {
      //   method: "POST",
      // });

      const response = await fetch(
        `${API_CHAT_URL}/rooms/${roomId}/close/`,
        {
          method: "POST",
          credentials: "include", // ✅ REQUIRED
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (response.ok) {
        setRooms(prev =>
          prev.map(room =>
            room.id === roomId ? { ...room, status: "closed" } : room
          )
        );
        console.log(`✅ Room ${roomId} closed successfully`);
      } else {
        console.error("Failed to close room on server");
        alert("Failed to close room. Please try again.");
        fetchRooms();
      }
    } catch (error) {
      console.error("Error closing room:", error);
      alert("Error closing room. Please check your connection.");
    }
  };

  const getRoomDisplayName = (room) => {
    if (room.participant1) {
      return room.participant1.split('@')[0];
    }
    if (room.participant2) {
      return room.participant2.split('@')[0];
    }
    return `Room ${room.id.substring(0, 8)}`;
  };

  const getParticipantInfo = (room) => {
    const isAssigned = room.participant2 !== null;
    const client = room.participant1 || "Unknown";
    const coordinator = room.participant2 || "Unassigned";
    
    return {
      isAssigned,
      client,
      coordinator
    };
  };

  // Clean up WebSocket connections on unmount
  useEffect(() => {
    return () => {
      Object.entries(socketsRef.current).forEach(([roomId, ws]) => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.close(1000, "Component unmounting");
        }
      });
    };
  }, []);

  // Refresh rooms every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRooms();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
      <h2>Coordinator Chat Dashboard</h2>
      <div style={{ marginBottom: "20px" }}>
        <button 
          onClick={fetchRooms}
          style={{
            padding: "8px 16px",
            background: "#4caf50",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          Refresh Rooms
        </button>
      </div>
      
      {rooms.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          No active chat rooms
        </div>
      ) : (
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))", 
          gap: "20px"
        }}>
          {rooms.map((room) => {
            const { isAssigned, client, coordinator } = getParticipantInfo(room);
            const isConnected = readyRef.current[room.id];
            const isOpen = room.status === "active";
            const displayName = getRoomDisplayName(room);

            return (
              <div
                key={room.id}
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "15px",
                  height: "400px",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  opacity: isOpen ? 1 : 0.7
                }}
              >
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center", 
                  marginBottom: "10px",
                  paddingBottom: "10px",
                  borderBottom: "1px solid #eee"
                }}>
                  <div>
                    <b style={{ fontSize: "16px" }}>{displayName}</b>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                      Client: {client !== "Unknown" ? client.split('@')[0] : "Unknown"}
                      {coordinator !== "Unassigned" && (
                        <> • Coordinator: {coordinator.split('@')[0]}</>
                      )}
                      {!isAssigned && (
                        <span style={{ color: "#ff9800", marginLeft: "8px" }}>
                          ⏳ Unassigned
                        </span>
                      )}
                      <div style={{ fontSize: "10px", color: "#888", marginTop: "2px" }}>
                        {room.loadedViaApi ? "📡 API Loaded" : "🔌 WebSocket Connected"}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ 
                      width: "10px", 
                      height: "10px", 
                      borderRadius: "50%",
                      backgroundColor: 
                        !isOpen ? "#f44336" :
                        isConnected ? "#4caf50" :
                        room.connecting ? "#ff9800" : "#9e9e9e"
                    }} />
                    
                    <span style={{ 
                      fontSize: "12px",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      backgroundColor: 
                        !isOpen ? "#ffebee" :
                        isConnected ? "#e8f5e8" : "#fff3e0",
                      color: 
                        !isOpen ? "#d32f2f" :
                        isConnected ? "#2e7d32" : "#ef6c00"
                    }}>
                      {!isOpen ? "Closed" :
                       isConnected ? "Connected" :
                       room.connecting ? "Connecting..." : "View Only"}
                    </span>
                    
                    {isOpen && !isConnected && (
                      <button 
                        onClick={() => connectToRoom(room.id)}
                        style={{ 
                          padding: "4px 12px", 
                          fontSize: "12px",
                          backgroundColor: "#2196f3",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer"
                        }}
                      >
                        Connect
                      </button>
                    )}
                    
                    {isOpen && (
                      <button 
                        onClick={() => closeRoom(room.id)}
                        style={{ 
                          padding: "4px 12px", 
                          fontSize: "12px",
                          backgroundColor: "#f44336",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer"
                        }}
                      >
                        Close
                      </button>
                    )}
                  </div>
                </div>

                <div 
                  ref={el => (containerRefs.current[room.id] = el)}
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "10px",
                    backgroundColor: "#fafafa",
                    borderRadius: "6px",
                    marginBottom: "15px"
                  }}
                >
                  {room.messages.length > 0 ? (
                    room.messages.map((m, i) => (
                      <div 
                        key={m.message_id || i} 
                        style={{ 
                          marginBottom: "10px",
                          padding: "8px 12px",
                          backgroundColor: m.sender === "Bot" ? "#e3f2fd" : 
                                         m.sender?.includes("coordinator") ? "#e8f5e8" : 
                                         "#fff",
                          borderRadius: "6px",
                          borderLeft: `3px solid ${
                            m.sender === "Bot" ? "#2196f3" :
                            m.sender?.includes("coordinator") ? "#4caf50" : "#9c27b0"
                          }`
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <b style={{ 
                            color: 
                              m.sender === "Bot" ? "#1976d2" :
                              m.sender?.includes("coordinator") ? "#2e7d32" : "#7b1fa2"
                          }}>
                            {m.sender}:
                          </b>
                          {m.timestamp && (
                            <span style={{ fontSize: "11px", color: "#757575" }}>
                              {new Date(m.timestamp).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </span>
                          )}
                        </div>
                        <div style={{ marginTop: "4px", wordBreak: "break-word" }}>
                          {m.message}
                        </div>
                        {/* {m.file && (
                          <div style={{ marginTop: "6px" }}>
                            <a 
                              href={m.file} 
                              target="_blank" 
                              rel="noreferrer"
                              style={{ 
                                color: "#2196f3", 
                                textDecoration: "none",
                                fontSize: "13px"
                              }}
                            >
                              📎 Download File
                            </a>
                          </div>
                        )} */}
                        {m.file_key || m.file && (
                          <button
                            onClick={() => openFile(m.file_key || m.file)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#2196f3",
                              cursor: "pointer",
                              fontSize: "13px"
                            }}
                          >
                            📎 Download File
                          </button>
                        )}

                      </div>
                    ))
                  ) : (
                    <div style={{ 
                      textAlign: "center", 
                      color: "#9e9e9e", 
                      padding: "30px 20px"
                    }}>
                      No messages yet.
                    </div>
                  )}
                  <div ref={el => (messageEndRefs.current[room.id] = el)} />
                </div>

                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
  
                  {/* 📎 FILE UPLOAD BUTTON */}
                  <label
                    style={{
                      cursor: isConnected && isOpen ? "pointer" : "not-allowed",
                      opacity: isConnected && isOpen ? 1 : 0.5,
                      fontSize: "18px",
                      padding: "0 6px"
                    }}
                  >
                    📎
                    <input
                      type="file"
                      hidden
                      disabled={!isConnected || !isOpen}
                      onChange={(e) => uploadFile(room.id, e.target.files[0])}
                    />
                  </label>
                
                  {/* MESSAGE INPUT */}
                  <input
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      border: `1px solid ${
                        !isOpen ? "#ddd" :
                        isConnected ? "#4caf50" : "#ff9800"
                      }`,
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none"
                    }}
                    disabled={!isConnected || !isOpen}
                    value={inputs[room.id] || ""}
                    placeholder={
                      !isOpen ? "Chat is closed" :
                      !isConnected ? "Click Connect to join chat" :
                      "Type message..."
                    }
                    onChange={(e) =>
                      setInputs({ ...inputs, [room.id]: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && inputs[room.id]?.trim()) {
                        sendMessage(room.id);
                      }
                    }}
                  />
                
                  {/* SEND BUTTON */}
                  <button
                    style={{
                      padding: "10px 20px",
                      backgroundColor: isConnected && isOpen ? "#4caf50" : "#ccc",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: isConnected && isOpen ? "pointer" : "not-allowed"
                    }}
                    disabled={!isConnected || !isOpen || !inputs[room.id]?.trim()}
                    onClick={() => sendMessage(room.id)}
                  >
                    Send
                  </button>
                
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}